/**
 * Fraud monitoring API (backend `fraud` app, /api/fraud/...).
 * See eventpay-backend/FRAUD_CONTROLS.md section 4 for the contract.
 */

export type FraudMode = 'shadow' | 'declines' | 'enforce'
export type FraudProfile = 'strict' | 'standard' | 'relaxed' | 'custom'
export type FraudDecision = 'allow' | 'step_up' | 'decline' | 'blocked'
export type FraudOutcome = 'pending' | 'succeeded' | 'declined' | 'step_up_pending' | 'refused' | 'error'
export type FraudBlockScope = 'account' | 'organization' | 'event'
export type FraudBlockReason =
  | 'chargeback' | 'stolen_card' | 'suspected_fraud' | 'card_testing'
  | 'policy_violation' | 'auto_decline' | 'manual' | 'other'
export type ChargebackState = 'received' | 'pending' | 'won' | 'lost' | 'accepted'

export interface FraudSignal {
  code: string
  weight: number
  detail: string
  hard: 'step_up' | 'decline' | null
}

export interface FraudUserSummary {
  id: number
  first_name: string
  last_name: string
  email: string
  phone_number: string | null
  date_joined: string | null
  is_active: boolean
}

/** The card a card block was placed on: brand and last four, never the fingerprint. */
export interface FraudBlockCard {
  brand: string
  last4: string
  card_type: string
  wallet: string
}

export type FraudBlockKind = 'attendee' | 'card'

export interface FraudActiveBlock {
  id: number
  /** 'card': the physical card by fingerprint, whoever holds it; the account is untouched. */
  kind?: FraudBlockKind
  instrument_id?: string
  card?: FraudBlockCard | null
  scope: FraudBlockScope
  reason: FraudBlockReason
  notes: string
  freeze_spending: boolean
  created_at: string
  expires_at: string | null
  created_by: number | null
}

export interface FraudRankedUser {
  user: FraudUserSummary
  score: number
  decision: FraudDecision
  /** An operator's allowance covers this account here: nothing is scored. */
  trusted: boolean
  signals: FraudSignal[]
  reloads: {
    count: number
    amount: string
    tokens: number
    count_window: number
    amount_24h: string
    first_at: string | null
    last_at: string | null
  }
  cards: {
    /** Distinct cards charged in the last 30 days, all events. */
    distinct: number
    /** Every card ever charged or saved on the account; a weak signal. */
    all_time?: number
    added_24h: number
    shared_with_users: number
    shared_with_flagged: boolean
  }
  transfers_out: { count: number; tokens: number }
  balance: number
  declines_recent: number
  chargebacks: number
  account_age_hours: number | null
  block: FraudActiveBlock | null
}

export interface FraudAssessment {
  id: number
  created_at: string
  user_id: number | null
  user_name: string | null
  event_id: number | null
  kind: string
  amount: string
  num_tokens: number | null
  currency: string
  score: number
  decision: FraudDecision
  enforced: boolean
  policy_mode: string
  signals: FraudSignal[]
  step_up_satisfied: boolean
  outcome: FraudOutcome
  failure_code: string
  instrument_id: string
  ip_address: string | null
  transaction_id: number | null
  resolved_at: string | null
}

/**
 * The event's policy row. `profile` bundles the thresholds; a non-null
 * threshold overrides the profile, 0 switches that trigger off.
 */
export interface FraudPolicyOverrides {
  profile: FraudProfile
  mode: FraudMode | null
  require_3ds_for_all: boolean | null
  step_up_spend_amount: string | null
  step_up_reload_count: number | null
  step_up_distinct_cards: number | null
  step_up_single_amount: string | null
  step_up_velocity_count: number | null
  step_up_velocity_minutes: number | null
  step_up_score: number | null
  decline_score: number | null
  max_daily_card_amount: string | null
  transfer_lock_minutes: number | null
  auto_block_on_decline: boolean | null
  notes: string
  updated_at?: string
  updated_by?: number | null
}

export interface FraudPolicyEffective {
  mode: FraudMode
  require_3ds_for_all: boolean
  step_up_spend_amount: string | null
  step_up_reload_count: number | null
  step_up_distinct_cards: number | null
  step_up_single_amount: string | null
  step_up_velocity_count: number | null
  step_up_velocity_minutes: number | null
  step_up_score: number | null
  decline_score: number | null
  max_daily_card_amount: string | null
  transfer_lock_minutes: number | null
  auto_block_on_decline: boolean
  profile: FraudProfile
  has_override: boolean
  /** The policy tables could not be read: code defaults in shadow, nothing enforced. */
  degraded?: boolean
}

export interface FraudProfileOption {
  value: FraudProfile
  label: string
  description: string
  /** Thresholds the profile bundles; empty for `custom`. */
  thresholds: Record<string, string | number | boolean | null>
}

export interface FraudPolicyResponse {
  event_id: number
  profile: FraudProfile
  profiles: FraudProfileOption[]
  overrides: FraudPolicyOverrides | null
  effective: FraudPolicyEffective
  defaults: Record<string, string | number | boolean | null>
  modes: FraudMode[]
}

export interface FraudOverviewTiles {
  card_reloads: number
  card_reload_amount: number
  card_reload_users: number
  assessments: number
  step_ups: number
  step_ups_enforced: number
  three_ds_passed: number
  declines: number
  refused: number
  provider_declined: number
  blocked_attempts: number
  flagged_users: number
  blocked_users: number
  /** Distinct trusted attendees, and trusted cards, at the event. */
  trusted_users: number
  trusted_cards: number
  chargebacks: number
  chargebacks_open: number
  chargebacks_lost: number
  chargeback_amount: number
}

export interface FraudOverview {
  generated_at: string
  event_id: number
  currency: string
  window_minutes: number
  policy: FraudPolicyEffective
  tiles: FraudOverviewTiles
  top_users: FraudRankedUser[]
  feed: FraudAssessment[]
}

export interface FraudUsersPage {
  generated_at: string
  event_id: number
  policy_mode: FraudMode
  count: number
  offset: number
  limit: number
  results: FraudRankedUser[]
}

export interface FraudAssessmentsPage {
  generated_at: string
  next_since: string | null
  results: FraudAssessment[]
}

export interface FraudCardOnFile {
  id: number
  instrument_id: string
  brand: string
  last4: string
  bin: string
  card_type: string
  issuer_country: string
  three_ds_verified_at: string | null
  can_step_up_on_file: boolean
  created_at: string
  disabled_at: string | null
  shared_with_users: number
  /** An active card allowance covers this card at the event. */
  trusted: boolean
  /** APPLE_PAY / GOOGLE_PAY for a wallet's per-charge instrument, else ''. */
  wallet?: string
  /** Instruments this row stands for: a wallet mints one per charge. */
  instruments?: number
  /** An active card block covers this card at the event (it beats trust). */
  blocked?: boolean
  block_id?: number | null
  /** The provider's $0 verification when the card was added: succeeded / failed / unsupported / error / ''. */
  verification_state?: string
  /** The issuer's failure code when the verification failed. */
  verification_failure_code?: string
}

export interface FraudRecentReload {
  id: number
  timestamp: string
  tokens: number
  charged: number
  /** The Finix transfer id (TRxxx) behind the charge. */
  provider_id: string
  /** The normalized card key: an instrument id, or `type:last4` for a wallet. */
  card: string | null
  refunded: boolean | number | null
  /** The card behind the charge, from the mirror; absent on an older backend. */
  instrument_id?: string
  card_brand?: string
  card_last4?: string
  card_wallet?: string
}

export interface FraudUserBlock extends FraudActiveBlock {
  active: boolean
  lifted_at: string | null
  lift_notes: string
}

// The detail row replaces the ranked row's chargeback COUNT with the records.
/** The attendee-detail chargeback rows: ids only, no names (ranking.chargeback_payload). */
export interface FraudUserDetailChargeback {
  id: number
  user_id: number | null
  event_id: number | null
  transaction_id: number | null
  provider: string
  provider_dispute_id: string
  provider_transfer_id: string
  amount: string
  currency: string
  reason: string
  state: ChargebackState
  received_at: string
  respond_by: string | null
  resolved_at: string | null
  notes: string
  created_by: number | null
  created_at: string
}

export interface FraudUserDetail extends Omit<FraudRankedUser, 'chargebacks'> {
  features: Record<string, string | number | boolean | null>
  recent_reloads: FraudRecentReload[]
  cards_on_file: FraudCardOnFile[]
  assessments: FraudAssessment[]
  blocks: FraudUserBlock[]
  allowances: FraudUserAllowance[]
  chargebacks: FraudUserDetailChargeback[]
  recent_transfers: { id: number; timestamp: string; tokens: number; counterparty_id: number | null }[]
}

export interface FraudBlock {
  id: number
  kind: FraudBlockKind
  user: number | null
  user_name: string | null
  instrument_id: string
  card: FraudBlockCard | null
  scope: FraudBlockScope
  event: number | null
  event_name: string | null
  organization: number | null
  organization_name: string | null
  reason: FraudBlockReason
  notes: string
  freeze_spending: boolean
  created_by: number | null
  created_at: string
  expires_at: string | null
  lifted_at: string | null
  lifted_by: number | null
  lift_notes: string
  source_assessment: number | null
  is_active: boolean
}

export interface FraudChargeback {
  id: number
  user: number | null
  user_name: string | null
  event: number | null
  event_name: string | null
  transaction: number | null
  provider: string
  provider_dispute_id: string
  provider_transfer_id: string
  amount: string
  currency: string
  reason: string
  state: ChargebackState
  received_at: string
  respond_by: string | null
  resolved_at: string | null
  notes: string
  created_by: number | null
  created_at: string
  updated_at: string
}

export type DisputeRecommendation = 'challenge' | 'accept' | 'needs_human'
/** Our answer to a Finix dispute: '' none yet; -ing while the Finix call is in flight (or died mid-call). */
export type DisputeResponseAction = '' | 'submitting' | 'submitted' | 'accepting' | 'accepted'

/**
 * How a Finix dispute is being answered. The chargebacks list carries these
 * for EventPay staff only; an organizer's list has none of them.
 */
export interface DisputeResponseFields {
  reason_code: string
  /** Finix's response_state: NEEDS_RESPONSE, RESPONDED, ACCEPTED, NO_RESPONSE_ALLOWED, UNKNOWN or ''. */
  provider_response_state: string
  /** What will be acted on: the drafted recommendation, or staff's override of it. */
  recommendation: DisputeRecommendation | ''
  computed_recommendation: DisputeRecommendation | ''
  recommendation_overridden: boolean
  recommendation_reasons: string[]
  /** This charge itself was 3-D Secure authenticated: the strongest case. */
  high_priority: boolean
  three_ds_authenticated: boolean
  has_evidence: boolean
  /** The dispute matched a charge in our ledger (else the packet has nothing to argue). */
  linked: boolean
  /** Why answering needs `override` (empty when it can be answered as drafted). */
  blockers: string[]
  evidence_built_at: string | null
  evidence_uploaded_at: string | null
  response_note: string
  response_action: DisputeResponseAction
  response_claimed_at: string | null
  submitted_at: string | null
  responded_by: number | null
  /** The account's live token balance at the event: what an accept could still take back. */
  balance_now: number | null
  reversed_at: string | null
  reversed_tokens: number
  unreversed_tokens: number
  reversal_transaction: number | null
}

/** A chargeback row as staff see it (organizers get the plain fields only). */
export type StaffChargeback = FraudChargeback & Partial<DisputeResponseFields>

export interface DisputeDraft extends FraudChargeback, DisputeResponseFields {
  evidence_facts: Record<string, unknown> | null
}

export interface DisputeReversal {
  reversed_tokens: number
  spent_tokens: number
  transaction_id: number | null
  note?: string
}

export interface FraudAllowance {
  id: number
  event: number
  kind: 'attendee' | 'card'
  user: number | null
  user_name: string | null
  instrument_id: string
  card: { brand: string; last4: string; card_type: string; user: number | null } | null
  reason: string
  created_by: number | null
  created_at: string
  expires_at: string | null
  revoked_at: string | null
  revoked_by: number | null
  is_active: boolean
  /** On create: the active block that still wins over this trust, if any. */
  blocked_by?: FraudBlock | null
  warning?: string | null
}

/** The attendee page's view of an allowance covering the account or one of its cards. */
export interface FraudUserAllowance {
  id: number
  kind: 'attendee' | 'card'
  user_id: number | null
  instrument_id: string
  reason: string
  active: boolean
  created_at: string
  expires_at: string | null
  revoked_at: string | null
  created_by: FraudUserSummary | null
}

// ---------------------------------------------------------------------------
// EventPay staff (Admin → Fraud)
// ---------------------------------------------------------------------------

export interface FraudPlatformSettings {
  mode: FraudMode
  default_profile: FraudProfile
  notes: string
  updated_at: string
  updated_by: number | null
}

export interface FraudPlatformResponse {
  settings: FraudPlatformSettings
  modes: FraudMode[]
  profiles: { value: FraudProfile; label: string; description: string }[]
  evervault_configured: boolean
}

export interface FraudAdminEvent {
  id: number
  name: string
  organization: { id: number; name: string } | null
  start_date_time: string | null
  end_date_time: string | null
  phase: 'live' | 'upcoming' | 'ended'
  currency: string
  payment_gateway: string
  enable_evervault: boolean
  policy: { profile: FraudProfile; mode: FraudMode; mode_override: FraudMode | null; require_3ds_for_all: boolean }
  window: {
    card_reloads: number
    card_reload_amount: number
    assessments: number
    step_ups: number
    refused: number
    provider_declined: number
    blocked_attempts: number
  }
  trusted: number
  active_blocks: number
  chargebacks: { count: number; open: number }
}

export interface FraudAdminEventsResponse {
  generated_at: string
  window_minutes: number
  platform_mode: FraudMode
  results: FraudAdminEvent[]
}

export interface FraudAdminUser extends FraudUserSummary {
  events: number
  last_seen: string | null
  active_blocks: number
  chargebacks: number
}

/** Cross-event profile of one account (`/fraud/users/{id}/`). */
export interface FraudUserProfile {
  user: FraudUserSummary
  events: (FraudRankedUser & { event: { id: number; name: string; currency: string } })[]
  blocks: FraudBlock[]
  chargebacks: FraudChargeback[]
  cards_on_file: {
    instrument_id: string
    wallet?: string
    instruments?: number
    brand: string
    last4: string
    card_type: string
    three_ds_verified_at: string | null
    created_at: string
  }[]
}

/** Exactly one of `user`, `instrument_id`, `fingerprint`. */
export interface CreateFraudAllowanceInput {
  user?: number | null
  instrument_id?: string
  fingerprint?: string
  reason?: string
  expires_at?: string | null
}

/** A card reload at the event, as the chargeback picker lists it. */
/** An account holding a card, as this organization may see it. */
export interface FraudCardAccount {
  user: FraudUserSummary
  /** Instruments of this card on the account (a wallet mints one per charge). */
  instruments: number
  added_at: string
  wallet: string
  three_ds_verified: boolean
  verification_state: string
  verification_failure_code: string
  charges_here: number
  last_charge_at: string | null
  blocked: boolean
  block_id: number | null
  trusted: boolean
  disabled: boolean
}

/** One physical card: the accounts holding it here, others only counted. */
export interface FraudCardDetail {
  card: {
    instrument_id: string
    brand: string
    last4: string
    bin: string
    card_type: string
    issuer_country: string
    wallet: string
    instruments: number
    blocked: boolean
    block_id: number | null
    trusted: boolean
    verification_state: string
    verification_failure_code: string
  }
  accounts: FraudCardAccount[]
  /** Holders with no footprint at this organization's events. */
  other_accounts: number
}

export interface FraudReload {
  id: number
  timestamp: string
  user: FraudUserSummary | null
  tokens: number
  charged: number
  currency: string
  card: { brand: string; last4: string; wallet?: string } | null
  instrument_id: string
  provider_transfer_id: string
  refunded: boolean | number | null
  chargeback_id: number | null
}

/** The charge at Finix and any dispute filed against it. */
export interface ChargeLookup {
  transaction: FraudReload | null
  transfer: {
    id: string
    amount: string | null
    currency: string
    state: string
    type: string
    created_at: string | null
    failure_code: string
    failure_message: string
    instrument_id: string
  } | null
  disputes: {
    id: string
    transfer_id: string
    reason: string
    state: string
    mapped_state: ChargebackState
    amount: string | null
    respond_by: string | null
    occurred_at: string | null
    existing_chargeback: FraudChargeback | null
  }[]
}

export interface CreateFraudBlockInput {
  /** Exactly one of `user` or `instrument_id`. */
  user?: number
  /** Block the physical card (by its fingerprint) this instrument belongs to. */
  instrument_id?: string
  reason: FraudBlockReason
  notes?: string
  expires_at?: string | null
  freeze_spending?: boolean
  scope?: FraudBlockScope
  event?: number | null
  organization?: number | null
}

export interface CreateChargebackInput {
  transaction?: number | null
  provider_transfer_id?: string
  provider_dispute_id?: string
  amount?: string | null
  reason?: string
  state?: ChargebackState
  respond_by?: string | null
  received_at?: string | null
  notes?: string
  block_user?: boolean
  block_scope?: FraudBlockScope
}

/**
 * The profiles an organizer can pick without a server round-trip (the new
 * event form). The policy endpoint returns the same catalogue with the
 * exact thresholds.
 */
export const FRAUD_PROFILES: { value: Exclude<FraudProfile, 'custom'>; label: string; description: string }[] = [
  { value: 'strict', label: 'Strict', description: 'Challenge early and cap hard: 3-D Secure from the third card charge or 150 spent (event currency), a 500 daily cap, a refused charge blocks the attendee.' },
  { value: 'standard', label: 'Standard (recommended)', description: '3-D Secure from the fourth card charge or 250 spent (event currency), a fourth card in a month, or three charges in an hour.' },
  { value: 'relaxed', label: 'Relaxed', description: 'The organizer accepts more risk: 3-D Secure from the eighth charge or 750 spent (event currency), a sixth card in a month, a short transfer lock.' },
]

export const FRAUD_BLOCK_REASONS: { value: FraudBlockReason; label: string }[] = [
  { value: 'suspected_fraud', label: 'Suspected fraud' },
  { value: 'stolen_card', label: 'Stolen card' },
  { value: 'card_testing', label: 'Card testing' },
  { value: 'chargeback', label: 'Chargeback received' },
  { value: 'policy_violation', label: 'Policy violation' },
  { value: 'manual', label: 'Manual' },
  { value: 'other', label: 'Other' },
]

export const CHARGEBACK_STATES: { value: ChargebackState; label: string }[] = [
  { value: 'received', label: 'Received' },
  { value: 'pending', label: 'Pending' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'accepted', label: 'Accepted' },
]
